<template>
  <fts-dropdown-select
    append-to-body
    :placement="placement"
    prevent-overflow-alt-axis
    prevent-overflow-tether-off
    :offset="[0, 6]"
    content-class="dropdown-menu dropdown-menu-w-sm dropdown-menu--sticky fts-form-design-shortcut-dropdown"
    ref="dropdownSelect"
    @content:keydown-enter="optionPointerSelect"
    @content:keydown-up="optionPointerUp"
    @content:keydown-down="optionPointerDown"
    @selected="onSelect"
    @show="onShow"
    @hide="onHide"
  >
    <div slot="toggle">
      <slot>(default)</slot>
    </div>
    <template #menu="{ select }">
      <div class="dropdown-filter" :class="{'dropdown-filter--searching': searching}">
        <div class="fts-form-control-filter">
          <input
            class="fts-form-control"
            v-model="filterKeyword"
            placeholder="Search something for..."
            @input="onInput"
            @keydown.up.prevent
            @keydown.down.prevent
            ref="filterKeywordInput"
          />
          <div class="search-icon">
            <fts-spinner :size="14" show v-if="searching" />
            <font-awesome-icon :icon="['fas', 'search']" fixed-width v-else />
          </div>
        </div>
      </div>

      <template v-for="(x, index) in getAllOptions">
        <div :key="`label-${index}`" class="dropdown-label" v-if="x.type === 'label'">
          {{x.value}}
        </div>

        <div :key="`divider-${index}`" class="dropdown-divider" v-else-if="x.type === 'divider'"></div>

        <fts-tippy
          :key="`information-type-${index}-${x.value.id}`"
          :content="x.value.hint"
          :ref="`dropdown-option-${index}`"
          v-else-if="x.type === 'create'"
        >
          <div
            class="dropdown-option"
            :class="[
              x.value.system ? 'dropdown-option--system' : 'dropdown-option--information',
              {'dropdown-option--active': selectedIndex === index}
            ]"
            data-name="information-type"
            @mouseenter="selectedIndex=index"
            @click="select(x)"
          >
            <div class="dropdown-option-icon" v-if="x.value.icon">
              <i :class="x.value.icon"></i>
            </div>
            <div>{{ x.value.name }}</div>
          </div>
        </fts-tippy>

        <div
          :key="`element-${index}`"
          class="dropdown-option"
          :class="{'dropdown-option--active': selectedIndex === index}"
          data-name="element"
          @mouseenter="selectedIndex=index"
          @click="select(x)"
          :ref="`dropdown-option-${index}`"
          v-else-if="x.type === 'element'"
        >
          <div>
            {{x.value._name}}
            <fts-release-badge class="fts-align-top" v-if="x.value.beta" />
          </div>
        </div>

        <div
          :key="x.value.id"
          class="dropdown-option"
          :class="[
            x.value.system ? 'dropdown-option--system' : 'dropdown-option--information',
            {'dropdown-option--active': selectedIndex === index}
          ]"
          @mouseenter="selectedIndex=index"
          @click="select(x)"
          data-name="information"
          :ref="`dropdown-option-${index}`"
          v-else-if="x.type === 'information'"
        >
          <div class="dropdown-option-icon" v-if="informationIcon(x.value.type)">
            <i :class="informationIcon(x.value.type)"></i>
          </div>
          <div>
            {{x.value.displayName}}
            <fts-release-badge class="fts-align-top" v-if="x.value.beta" />
          </div>
        </div>

        <div
          :key="`suggest-${index}`"
          class="dropdown-option dropdown-option--information"
          :class="{'dropdown-option--active': selectedIndex === index}"
          @mouseenter="selectedIndex=index"
          @click="select(x)"
          data-name="suggest"
          :ref="`dropdown-option-${index}`"
          v-else-if="x.type === 'suggest'"
        >
          <div class="dropdown-option-icon" v-if="x.value.icon">
            <i :class="x.value.icon"></i>
          </div>
          <div class="dropdown-option-emoji" v-if="x.value.emoji">
            <fts-moji :emoji="x.value.emoji" :size="14" />
          </div>
          <div>{{ x.value.name }}</div>
          <div class="dropdown-option-ai-label">
            <font-awesome-icon :icon="['fas', 'wand-sparkles']" />AI
          </div>
          <div class="dropdown-option-kbd-hint" v-if="selectedIndex === index">
            <kbd>Enter</kbd> to create
          </div>
        </div>

        <div
          :key="`suggest-name-${index}`"
          class="dropdown-option"
          :class="{'dropdown-option--active': selectedIndex === index}"
          @mouseenter="selectedIndex=index"
          @click="select(x)"
          data-name="suggest-name"
          :ref="`dropdown-option-${index}`"
          v-else-if="x.type === 'suggest-name'"
        >
          <span class="suggest-option-prefix">Create</span>
          <div>{{ x.value.name }}...</div>
        </div>
      </template>
      <!-- selected options -->
      <template v-if="getSelectedOptions && getSelectedOptions.length">
        <div class="dropdown-label">
          Selected information
          <span class="dropdown-label-badge">In Used</span>
        </div>
        <div
          v-for="option in getSelectedOptions"
          :key="option.key"
          class="dropdown-option"
          :class="[
            option.system ? 'dropdown-option--system' : 'dropdown-option--information',
            {'dropdown-option--disabled': !deselectable}
          ]"
          data-name="selected"
          @click="deselectable && select({ type: 'deselect', option: option})"
        >
          <div class="dropdown-option-icon" v-if="informationIcon(option.type)">
            <i :class="informationIcon(option.type)"></i>
          </div>
          <div>
            {{option.displayName}}
            <fts-release-badge class="fts-align-top" v-if="option.beta" />
          </div>
        </div>
      </template>
      <!-- no options -->
      <div 
        class="dropdown-option-not-found"
        v-if="
          !searching &&
          !Boolean(getAllOptions && getAllOptions.length) &&
          !Boolean(getSelectedOptions && getSelectedOptions.length)
        "
      >
        No available options found
      </div>
    </template>
  </fts-dropdown-select>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import commons from '../commons/commons';
import dropdown from '../commons/dropdown';
import { Information } from '../commons/flags';

import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSTippy from '../elements/FTSTippy';
import FTSSpinner from '../elements/FTSSpinner';
import FTSMoji from '../elements/FTSMoji';

export default {
  components: {
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-tippy': FTSTippy,
    'fts-spinner': FTSSpinner,
    'fts-moji': FTSMoji
  },
  props: {
    modules: Array,
    options: Array,
    elements: Array,
    selectedOptions: Array,
    excludeTypes: Array,
    suggestion: Boolean,
    deselectable: Boolean,
    placement: {
      type: String,
      default: 'bottom'
    },
    disableCreate: Boolean,
    isLite: Boolean,
  },
  data: () => ({
    filterKeyword: null,
    searching: false,
    searchSuggestions: [],
    selectedIndex: 0
  }),
  methods: {
    onShow() {
      this.$nextTick(() => {
        this.$refs.filterKeywordInput.focus();
      });
    },
    onHide() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    onSelect({ type, value }) {
      if (type === 'create') {
        if (value.value) {
          this.$emit('option:create', value.value);
        } else if (value.callback) {
          if (typeof value.callback === 'function') {
            try {
              value.callback();
            } catch(err) {
              console.error(err);
            }
          } else {
            console.warn('value.callback is not a function.', value);
          }
        } else {
          const MAX_ATTEMPTS = 10;

          const normalize = (name) => String(name).toLowerCase().trim();

          const getDisplayName = (list, displayName) => {
            let usedNames = new Set();

            for (let i = 0; i < list.length; i++) {
              const item = list[i];
              if (item && item.displayName) {
                usedNames.add(normalize(item.displayName));
              }
            }

            for (let count = 0; count <= MAX_ATTEMPTS; count++) {
              const attemptName = count === 0 ? displayName : `${displayName} ${count}`;

              if (!usedNames.has(normalize(attemptName))) {
                return attemptName;
              }
            }

            return null;
          }

          let displayName = value.name;

          if (this.isLite) {
            const filterKeyword = this.filterKeyword && this.filterKeyword.trim();
            if (filterKeyword) {
              displayName = filterKeyword;
            }
          }

          const allOptions = [
            ...(Array.isArray(this.options) ? this.options : []),
            ...(Array.isArray(this.selectedOptions) ? this.selectedOptions : [])
          ];

          this.$emit('option:create', {
            displayName: getDisplayName(allOptions, displayName),
            type: value.id
          });
        }
      } else if (type === 'suggest') {
        let res = { type: value.id, displayName: value.name };
        if (value.id === 7 && value.connectionId && value.connection) {
          res.connectionId = value.connectionId;
          res.connection = value.connection;
        }
        this.$emit('option:create', res);
      } else if (type === 'suggest-name') {
        this.$emit('option:create', { displayName: value.name });
      } else if (type === 'information' || type === 'element') {
        this.$emit('option:selected', _.cloneDeep(value));
      } else if (type === 'deselect') {
        this.$emit('option:deselected', _.cloneDeep(value));
      }
    },
    informationIcon(type) {
      return commons.informationIcon(type);
    },
    optionPointerSelect() {
      if (this.selectedIndex > -1 && this.getAllOptions[this.selectedIndex]) {
        this.$refs.dropdownSelect.select(this.getAllOptions[this.selectedIndex]);
      }
    },
    optionPointerUp(item) {
      this.selectedIndex = dropdown.optionPointerUp({
        currentIndex: this.selectedIndex,
        options: this.getAllOptions,
        exclude: (x) => x && x.type !== 'label' && x.type !== 'divider',
        scrollEl: item.popperContentEl,
        refs: this.$refs
      });
    },
    optionPointerDown(item) {
      this.selectedIndex = dropdown.optionPointerDown({
        currentIndex: this.selectedIndex,
        options: this.getAllOptions,
        exclude: (x) => x && x.type !== 'label' && x.type !== 'divider',
        scrollEl: item.popperContentEl,
        refs: this.$refs
      });
    },
    debounceSearch: _.debounce(async (vm) => {
      const module = Array.isArray(vm.modules) && vm.modules.find((x) => 
        x.name && vm.filterKeyword && (x.name.toLowerCase().trim() === vm.filterKeyword.toLowerCase().trim())
      );
      if (module) {
        vm.searchSuggestions = [{
          type: 'suggest',
          value: {
            id: 7, /* to-do user profile return 20 */
            name: module.name,
            emoji: module.emoji,
            connection: module.name,
            connectionId: module.id,
          }
        }];
        vm.searching = false;
      } else if (vm.filterKeyword) {
        let suggestionTypes = [0];
        try {
          const res = await canvasDesignStore.dispatch('ai', {
            type: 'information',
            text: vm.filterKeyword
          });
          suggestionTypes = (commons.isArray(res.data) ? [...res.data] : [res.data]).filter((x) => commons.isInteger(x));
          if (vm.getExcludeTypes) {
            suggestionTypes = suggestionTypes.filter((x) => !vm.getExcludeTypes.some((y) => y == x));
          }
          if (!suggestionTypes.length) {
            suggestionTypes = [0];
          }
        } catch(err) {
          console.error(err);
        } finally {
          vm.searchSuggestions = suggestionTypes.map((x) => ({
            type: 'suggest',
            value: {
              id: x,
              name: vm.filterKeyword,
              icon: commons.informationIcon(x)
            }
          }));
          vm.searching = false;
        }
      }
    }, 800),
    onInput() {
      this.searchSuggestions = [];
      this.searching = false;
      if (this.isSuggestion && this.filterKeyword) {
        this.searching = true;
        this.debounceSearch(this);
      }
    },
    isHelperOption(value) {
      return value && (value.label === true || value.divider === true) && !value.fieldName;
    }
  },
  computed: {
    getExcludeTypes() {
      let types = Array.isArray(this.excludeTypes) ? this.excludeTypes : [];
      !authorizationStore.getters.isInternal && (types = [...new Set([...types, 15])]);
      return types;
    },
    informationTypeOptions() {
      return [
        'Text',
        'MultilineText',
        'YesNo',
        'Integer',
        'Number',
        'Money',
        'Date',
        'Module',
        'Images',
        'Files',
        'User',
        'Email',
        'Phone',
        'Select'
      ]
        .map((x) => ({
          id: Information[x].ID,
          name: Information[x].Name,
          icon: Information[x].Icon,
          hint: Information[x].Hint,
        }))
        .filter((x) => !this.getExcludeTypes.some((y) => y == x.id));
    },
    advancedInformationTypeOptions() {
      return [
        'DateTime',
        'Modules',
        'Location',
        'Image',
        'File',
        'Table',
        'Users',
        'Content',
        'Tag',
        'Signature',
        'URL'
      ]
        .map((x) => ({
          id: Information[x].ID,
          name: Information[x].Name,
          icon: Information[x].Icon,
          hint: Information[x].Hint,
        }))
        .filter((x) => !this.getExcludeTypes.some((y) => y == x.id));
    },
    getInformationTypeOptions() {
      const options = [...this.informationTypeOptions];

      if (this.isLite) {
        return options;
      }

      const otherTypes = [{ 
        id: 'moreTypes',
        name: 'more types...',
        hint: 'more types...',
        value: {}
      }];

      if (this.filterKeyword) {
        const keyword = this.filterKeyword.toLowerCase();

        return options
          .concat(this.advancedInformationTypeOptions, otherTypes)
          .filter(x => x.name && x.name.toLowerCase().includes(keyword));
      }

      return options.concat(otherTypes);
    },
    getElements() {
      const elements = Array.isArray(this.elements) ? this.elements : [];
      return this.filterKeyword ?
        elements.filter((x) =>
          (x._name && x._name.toLowerCase().indexOf(this.filterKeyword.toLowerCase()) !== -1)
        ) :
        elements;
    },
    getSuggestions() {
      let suggestions = [];
      if (this.isSuggestion && this.filterKeyword && !this.searching) {
        if (this.searchSuggestions && this.searchSuggestions.length) {
          suggestions = [...this.searchSuggestions];
        }

        if (!this.isLite) {
          suggestions = [
            ...suggestions,
            { type: 'suggest-name', value: { name: this.filterKeyword }}
          ];
        }
      }
      return suggestions;
    },
    getOptions() {
      let options = Array.isArray(this.options) ? this.options : [];
      if (this.filterKeyword) {
        options = options.reduce((accumulator, currentValue) => {
          if (this.isHelperOption(currentValue)) {
            const lastValue = accumulator.at(-1);
            if (
              lastValue && this.isHelperOption(lastValue) && (
                (
                  (currentValue.label && lastValue.label) ||
                  (currentValue.divider && lastValue.divider)
                )
              )
            ) {
              accumulator.splice(-1);
            }
            return [...accumulator, currentValue];           
          } else if (currentValue.displayName && currentValue.displayName.toLowerCase().indexOf(this.filterKeyword.toLowerCase()) !== -1) {
            return [...accumulator, currentValue];
          }
          return accumulator;
        }, []);

        if (options.length && this.isHelperOption(options.at(-1))) {
          options.splice(-1);
        }

        return options;
      }
      return options;
    },
    getSelectedOptions() {
      if (this.filterKeyword) {
        return this.selectedOptions && this.selectedOptions.filter((x) =>
          (x.displayName && x.displayName.toLowerCase().indexOf(this.filterKeyword.toLowerCase()) !== -1)
        );
      }
      return this.selectedOptions;
    },
    getAllOptions() {
      let options = [];
      if (!this.disableCreate && this.getInformationTypeOptions.length) {
        options = [
          ...options,
          { type: 'label', value: 'Information type' },
          ...this.getInformationTypeOptions.map((x) => ({ type: 'create', value: x }))
        ];
      }

      let informationOptions = [];
      if (this.getOptions.length) {
        if (!(this.getOptions[0].label && this.isHelperOption(this.getOptions[0]))) {
          informationOptions.push({ type: 'label', value: 'Information' });
        }
        informationOptions.push(
          ...this.getOptions.map((x) => {
            if (this.isHelperOption(x)) {
              if (x.divider) {
                return { type: 'divider' };
              } else {
                return { type: 'label', value: x.name || '(undefined)' };
              }
            } else {
              return { type: 'information', value: x };
            }
          })
        );
      }

      let elementOptions = [];
      if (this.getElements.length) {
        elementOptions = [
          { type: 'label', value: 'Elements' },
          ...this.getElements.map((x) => ({ type: 'element', value: x }))
        ];
      }

      let suggestionOptions = [];
      if (this.getSuggestions.length) {
        suggestionOptions = [
          { type: 'label', value: 'New Information' },
          ...this.getSuggestions
        ];
      }

      if (this.isLite) {
        options = [...suggestionOptions, ...informationOptions, ...options, ...elementOptions];
      } else {
        options = [...options, ...elementOptions, ...informationOptions, ...suggestionOptions];
      }

      this.selectedIndex = options.findIndex(x => x.type !== 'label' && x.type !== 'divider');

      return options;
    },
    isSuggestion() {
      const isExist = () => {
        if (this.filterKeyword) {
          try {
            return (
              this.getOptions && this.getOptions.some((x) => x.displayName.toLowerCase().trim() === this.filterKeyword.toLowerCase().trim()) ||
              this.getSelectedOptions && this.getSelectedOptions.some((x) => x.displayName.toLowerCase().trim() === this.filterKeyword.toLowerCase().trim())
            );
          } catch(err) { console.error(err); }
        }
        return false;
      }
      return !this.disableCreate && this.suggestion && !isExist();
    }
  }
}
</script>

<style lang="scss">
.fts-form-design-shortcut-dropdown.dropdown-menu {
  .suggest-option-prefix {
    margin-right: 0.375rem;
    color: var(--fts-default);
    font-size: 0.75rem;
    white-space: nowrap;
  }
}
</style>